// Architect rankings data - aggregates from golf_course_designers + golf_courses
import { supabase } from './supabase';
import { slugify } from './slugify';

const RANK_FIELDS = {
  England: 'england_rank_gl',
  Scotland: 'scotland_rank_gl',
  Wales: 'wales_rank_gl',
  Ireland: 'ireland_rank_gl',
  'Northern Ireland': 'ni_rank_gl',
  USA: 'usa_rank_gl',
};

const COUNTRY_SORT_ORDER = {
  England: 1,
  Scotland: 2,
  Ireland: 3,
  Wales: 4,
  'Northern Ireland': 5,
  USA: 6,
};

const TOP_100_THRESHOLD = 100;

function getNationalRank(course) {
  const field = RANK_FIELDS[course.course_country];
  if (!field || course[field] == null) return Infinity;
  const rank = Number(course[field]);
  return Number.isNaN(rank) || rank <= 0 ? Infinity : rank;
}

function sortCoursesByNationalRank(courses) {
  return [...courses].sort((a, b) => {
    const rankA = getNationalRank(a);
    const rankB = getNationalRank(b);
    if (rankA !== rankB) return rankA - rankB;
    const orderA = COUNTRY_SORT_ORDER[a.course_country] ?? 99;
    const orderB = COUNTRY_SORT_ORDER[b.course_country] ?? 99;
    return orderA - orderB;
  });
}

const EXCLUDED_DESIGNERS = ['a designer unknown'];

function isExcludedDesigner(name) {
  if (!name) return true;
  return EXCLUDED_DESIGNERS.includes(String(name).trim().toLowerCase());
}

function isDesignerInArray(arr, designerName) {
  if (!Array.isArray(arr) || !designerName) return false;
  const normalized = designerName.trim().toLowerCase();
  return arr.some(
    (item) =>
      (typeof item === 'string' ? item.trim() : String(item)).toLowerCase() ===
      normalized,
  );
}

function isTop100InCountry(course, country) {
  const field = RANK_FIELDS[country];
  if (!field || !course[field]) return false;
  const rank = Number(course[field]);
  return !Number.isNaN(rank) && rank > 0 && rank <= TOP_100_THRESHOLD;
}

function computePopularityScore(architect, countryCounts) {
  const volumeWeight = 2; // original counts 2x

  const weightedVolume = architect.originalCount * volumeWeight + architect.contributorCount;
  let qualitySum = 0;
  let qualityWeightSum = 0;

  for (const course of architect.courses) {
    const field = RANK_FIELDS[course.course_country];
    const rank = field ? course[field] : null;
    if (rank == null || rank <= 0) continue;

    const totalInCountry = countryCounts[course.course_country] || 1;
    const rankNum = Number(rank);
    if (Number.isNaN(rankNum) || totalInCountry < rankNum) continue;

    const pct = (totalInCountry - rankNum + 1) / totalInCountry;
    const w = course.role === 'original' ? 2 : 1;
    qualitySum += pct * w;
    qualityWeightSum += w;
  }

  const avgQuality = qualityWeightSum > 0 ? qualitySum / qualityWeightSum : 0;
  return { weightedVolume, qualityComponent: avgQuality };
}

/**
 * Fetch architect rankings with course counts, top counties, top 100 by country.
 * Only returns architects with 2+ total courses.
 * @param {{ courseCountry?: string, courseCounty?: string }} filters
 */
export async function fetchArchitectRankings(filters = {}) {
  const { courseCountry, courseCounty } = filters;

  const countries = ['England', 'Scotland', 'Wales', 'Ireland', 'Northern Ireland', 'USA'];
  const countPromises = countries.map((country) =>
    supabase
      .from('golf_courses')
      .select('*', { count: 'exact', head: true })
      .eq('course_country', country),
  );

  const [designersRes, coursesRes, ...countResList] = await Promise.all([
    supabase.from('golf_course_designers').select('course_designer, bio').order('course_designer', { ascending: true }),
    supabase.from('golf_courses').select(
      'course_name, course_uid, course_county, course_country, county_rank, latitude, longitude, combined_designer_original, designer_contributor, england_rank_gl, scotland_rank_gl, wales_rank_gl, ireland_rank_gl, ni_rank_gl, usa_rank_gl',
    ),
    ...countPromises,
  ]);

  if (designersRes.error) throw designersRes.error;
  if (coursesRes.error) throw coursesRes.error;

  const countryCourseCounts = {};
  countResList.forEach((res, i) => {
    const country = countries[i];
    if (res?.error) return;
    countryCourseCounts[country] = res?.count ?? 0;
  });

  // Deduplicate: multiple rows in golf_course_designers may spell the same architect
  // differently (e.g. "Alister MacKenzie" vs "Alister Mackenzie"). Use canonical
  // lowercase key and keep the first spelling as display name. Use first non-null bio.
  const designersSeen = new Map(); // canonical -> { displayName, bio }
  const designers = [];
  for (const d of designersRes.data || []) {
    const name = d.course_designer?.trim();
    if (!name || isExcludedDesigner(name)) continue;
    const canonical = name.toLowerCase();
    const existing = designersSeen.get(canonical);
    if (existing) {
      if (!existing.bio && d.bio) existing.bio = d.bio;
      continue;
    }
    designersSeen.set(canonical, { displayName: name, bio: d.bio || null });
    designers.push(name);
  }
  const bioByDesigner = {};
  for (const { displayName, bio } of designersSeen.values()) {
    if (bio) bioByDesigner[displayName] = bio;
  }
  const courses = coursesRes.data || [];

  // First pass: build full (unfiltered) architect data for popularity score - fixed across all filters
  const fullByDesigner = {};
  for (const designer of designers) {
    const fullOriginal = [];
    const fullContributor = [];
    for (const course of courses) {
      const isOriginal = isDesignerInArray(course.combined_designer_original, designer);
      const isContributor = isDesignerInArray(course.designer_contributor, designer);
      if (isOriginal) fullOriginal.push(course);
      if (isContributor) fullContributor.push(course);
    }
    const fullOriginalUids = new Set(fullOriginal.map((c) => c.course_uid));
    const fullContributorOnly = fullContributor.filter((c) => !fullOriginalUids.has(c.course_uid));
    const fullTotal = fullOriginal.length + fullContributorOnly.length;
    if (fullTotal < 2) continue;

    const fullOriginalWithRole = fullOriginal.map((c) => ({ ...c, role: 'original' }));
    const fullContributorWithRole = fullContributorOnly.map((c) => ({ ...c, role: 'contributor' }));
    const fullCourses = [
      ...sortCoursesByNationalRank(fullOriginalWithRole),
      ...sortCoursesByNationalRank(fullContributorWithRole),
    ];
    const fullArchitectData = {
      originalCount: fullOriginal.length,
      contributorCount: fullContributorOnly.length,
      courses: fullCourses,
    };
    const { weightedVolume, qualityComponent } = computePopularityScore(fullArchitectData, countryCourseCounts);
    fullByDesigner[designer] = { _popularityVolume: weightedVolume, _popularityQuality: qualityComponent };
  }

  const allFullVolumes = Object.values(fullByDesigner).map((d) => d._popularityVolume);
  const maxVolume = Math.max(...allFullVolumes, 1);
  const volW = 0.4;
  const qualW = 0.6;
  const popularityByDesigner = {};
  for (const [designer, data] of Object.entries(fullByDesigner)) {
    const volumeScore = (data._popularityVolume / maxVolume) * volW;
    const qualityScore = data._popularityQuality * qualW;
    popularityByDesigner[designer] = Math.round(Math.min(100, (volumeScore + qualityScore) * 100));
  }

  // Second pass: build filtered architect data for display (counts, courses, top100, top3Counties)
  const byDesigner = {};

  for (const designer of designers) {
    const originalCourses = [];
    const contributorCourses = [];
    const designerCountyCounts = {};
    const top100ByCountry = {};

    for (const course of courses) {
      const inCountry = !courseCountry || course.course_country === courseCountry;
      const inCounty = !courseCounty || course.course_county === courseCounty;
      if (!inCountry || !inCounty) continue;

      const isOriginal = isDesignerInArray(course.combined_designer_original, designer);
      const isContributor = isDesignerInArray(course.designer_contributor, designer);

      if (isOriginal) originalCourses.push(course);
      if (isContributor) contributorCourses.push(course);

      if (isOriginal || isContributor) {
        if (course.course_county) {
          designerCountyCounts[course.course_county] = (designerCountyCounts[course.course_county] || 0) + 1;
        }
        for (const country of Object.keys(RANK_FIELDS)) {
          if (isTop100InCountry(course, country)) {
            top100ByCountry[country] = (top100ByCountry[country] || 0) + 1;
          }
        }
      }
    }

    const originalUids = new Set(originalCourses.map((c) => c.course_uid));
    const contributorOnly = contributorCourses.filter((c) => !originalUids.has(c.course_uid));
    const total = originalCourses.length + contributorOnly.length;
    if (total < 2) continue;

    const top3Counties = Object.entries(designerCountyCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([county, count]) => ({ county, count }));

    const originalWithRole = originalCourses.map((c) => ({
      ...c,
      role: 'original',
      slug_course_name: slugify(c.course_name),
    }));
    const contributorWithRole = contributorOnly.map((c) => ({
      ...c,
      role: 'contributor',
      slug_course_name: slugify(c.course_name),
    }));
    const allCourses = [
      ...sortCoursesByNationalRank(originalWithRole),
      ...sortCoursesByNationalRank(contributorWithRole),
    ];

    const architectData = {
      name: designer,
      slug: slugify(designer),
      originalCount: originalCourses.length,
      contributorCount: contributorCourses.length,
      totalCount: total,
      top100ByCountry,
      top3Counties,
      courses: allCourses,
      bio: bioByDesigner[designer] || null,
    };
    architectData.popularityScore = popularityByDesigner[designer] ?? 0;
    byDesigner[designer] = architectData;
  }

  const allArchitects = Object.values(byDesigner);

  // Rank by original designs (descending), then contributions, then name
  allArchitects.sort((a, b) => {
    if (b.originalCount !== a.originalCount) return b.originalCount - a.originalCount;
    if (b.contributorCount !== a.contributorCount) return b.contributorCount - a.contributorCount;
    return (a.name || '').localeCompare(b.name || '');
  });

  return allArchitects;
}

/**
 * Fetch a single architect by slug. Returns null if not found or has < 2 courses.
 */
export async function fetchArchitectBySlug(slug) {
  const rankings = await fetchArchitectRankings({});
  if (!Array.isArray(rankings)) return null;
  return rankings.find((a) => a.slug === slug) || null;
}

/**
 * Check if an architect has 2+ courses (for link eligibility).
 */
export async function getArchitectSlugIfLinkable(designerName) {
  if (!designerName) return null;
  const rankings = await fetchArchitectRankings({});
  if (!Array.isArray(rankings)) return null;
  const architect = rankings.find(
    (a) => a.name.trim().toLowerCase() === designerName.trim().toLowerCase(),
  );
  return architect ? architect.slug : null;
}
